import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { AdminAccount } from 'src/app/types/Onboarding';
import { ProgressService } from '../../services/progress.service';
import { OnboardingService } from '../../services/onboarding.service';
import { ButtonComponent } from 'src/app/shared/components/button/button.component';
import { EMAIL_FORMAT_REGEX } from 'src/app/shared/util/validation.constants';

@Component({
  selector: 'app-club-admin-registration',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, InputTextModule, SelectModule, PasswordModule, ButtonComponent],
  templateUrl: './club-admin-registration.component.html',
  styleUrls: ['./club-admin-registration.component.scss'],
})
export class ClubAdminRegistrationComponent implements OnInit, OnDestroy {
  protected readonly router = inject(Router);
  protected readonly toastr = inject(ToastrService);
  protected readonly progressService = inject(ProgressService);
  protected readonly onboardingService = inject(OnboardingService);
  protected readonly destroy$ = new Subject<void>();

  private static readonly PHONE_FORMAT_REGEX = /^\+?[0-9]{7,15}$/;

  protected adminInformation!: AdminAccount;
  protected activities = signal<any[]>([]);
  protected submitted = signal(false);

  ngOnInit(): void {
    this.adminInformation = this.onboardingService.getAccountInformation().adminInformation;
    this.onboardingService.getActivities()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.activities.set(response.data.activities),
        error: () => {}
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected isValidPhone(v: unknown): boolean {
    return ClubAdminRegistrationComponent.PHONE_FORMAT_REGEX.test(String(v ?? ''));
  }

  protected isValidEmailFormat(v: string | undefined | null): boolean {
    return !!v && EMAIL_FORMAT_REGEX.test(v);
  }

  private getFirstInvalidFieldId(): string | null {
    if (!this.adminInformation.clubName?.trim()) { return 'clubName'; }
    if (!this.adminInformation.primaryActivity) { return 'primaryActivitySelect'; }
    if (!this.adminInformation.contactPersonName?.trim()) { return 'contactPerson'; }
    if (!this.adminInformation.phoneNumber) { return 'phoneNumber'; }
    if (!this.isValidPhone(this.adminInformation.phoneNumber)) { return 'phoneNumber'; }
    if (!this.adminInformation.email?.trim()) { return 'adminEmail'; }
    if (!this.isValidEmailFormat(this.adminInformation.email)) { return 'adminEmail'; }
    if (!this.adminInformation.password?.trim()) { return 'adminPasswordInput'; }
    if (this.adminInformation.password.length < 6) { return 'adminPasswordInput'; }
    return null;
  }

  private focusFirstInvalid(id: string): void {
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) { return; }
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }, 0);
  }

  protected onPhonePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const cleaned = pasted.replace(/[^0-9+]/g, '');
    const current = String(this.adminInformation.phoneNumber || '');
    (this.adminInformation as any).phoneNumber = current + cleaned;
  }

  protected nextPage(): void {
    this.submitted.set(true);

    const firstInvalidId = this.getFirstInvalidFieldId();
    if (firstInvalidId) {
      this.toastr.error('Please correct the highlighted fields');
      this.focusFirstInvalid(firstInvalidId);
      return;
    }

    const selectedActivity = this.activities().find(a => a.ActivityCode === this.adminInformation.primaryActivity);
    if (selectedActivity) {
      this.adminInformation.primaryActivityId = selectedActivity.ActivityId || selectedActivity.Id;
      this.adminInformation.primaryActivityName = selectedActivity.ActivityName;
    }
    if (this.adminInformation.email) {
      this.adminInformation.email = this.adminInformation.email.replace(/^mailto:/i, '');
    }

    // Check admin email uniqueness before proceeding
    this.onboardingService.checkEmailUnique(this.adminInformation.email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.data?.unique === false || res?.unique === false) {
            this.toastr.error('This email is already registered. Please use a different admin email.');
            return;
          }
          this.onboardingService.accountInformation.adminInformation = this.adminInformation;
          this.router.navigate(['/auth/onboarding/club-details']);
        },
        error: () => {
          // If check fails, proceed anyway — backend will catch it
          this.onboardingService.accountInformation.adminInformation = this.adminInformation;
          this.router.navigate(['/auth/onboarding/club-details']);
        }
      });
  }
}
